import { z } from "zod";
import { CAMPING_STYLES, KINDS } from "../../../constants";
import { PRICE_DURATIONS } from "../../../constants/price_durations";

export const EditSiteFormSchema = z.object({
  // Basic site information
  id: z.string().optional(),
  name: z.string().min(1, "Site name is required"),
  is_bookable: z.boolean(),
  camping_style: z.string(),
  kind: z.string(),
  
  // Capacity and dimensions
  max_rig_length: z.string().nullable(),
  camper_capacity: z.string().nullable(),
  minimum_capacity: z.string().nullable(),
  
  // Utilities
  electric: z.string().nullable(),
  water: z.boolean().nullable(),
  sewage: z.boolean().optional(),
  current_meter_reading: z.string().nullable().refine(
    (val) => !val || (val && !isNaN(Number(val)) && Number(val) >= 0),
    { message: "Meter reading must be a positive number" }
  ),
  
  // Text fields
  notes: z.string().nullable(),
  checkin_policy_additions: z.string().nullable(),
  
  // Amenities
  has_linens: z.boolean().nullable(),
  
  // Prices
  prices: z.array(
    z.object({
      id: z.string().optional(),
      amount_in_cents: z.string(),
      duration: z.string(),
      isActive: z.boolean().optional(),
    }).superRefine((val, ctx) => {
      if (val.isActive) {
        if (!val.amount_in_cents || isNaN(Number(val.amount_in_cents)) || Number(val.amount_in_cents) <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Price must be greater than 0",
            path: ["amount_in_cents"],
          });
        }
      }
    })
  ).refine(
    (arr) => arr.some((p) => p.isActive),
    { message: "At least one price must be enabled" }
  ),
  
  // Boolean features from SiteFeatures.ts
  fire_pit: z.boolean().optional(),
  has_grill_barbecue: z.boolean().optional(),
  pets: z.boolean().optional(),
  has_wifi: z.boolean().optional(),
  extra_car_space: z.boolean().optional(),
  premium_view: z.boolean().optional(),
  cable_tv: z.boolean().optional(),
  
  // Lodging features
  has_towels: z.boolean().optional(),
  has_private_bathroom: z.boolean().optional(),
  has_heating: z.boolean().optional(),
  has_air_conditioning: z.boolean().optional(),
  has_microwave: z.boolean().optional(),
  has_oven: z.boolean().optional(),
  has_stove: z.boolean().optional(),
  has_hotplate: z.boolean().optional(),
  has_refrigerator: z.boolean().optional(),
  has_tv: z.boolean().optional(),
  has_coffee_machine: z.boolean().optional(),
  
  // Bed features (numeric)
  number_of_single_beds: z.number().optional(),
  number_of_twin_beds: z.number().optional(),
  number_of_bunk_beds: z.number().optional(),
  number_of_futon_beds: z.number().optional(),
  number_of_full_beds: z.number().optional(),
  number_of_queen_beds: z.number().optional(),
  number_of_king_beds: z.number().optional(),
  
  // Additional boolean features from SiteProps.ts
  is_tentable: z.boolean().optional(),
  has_laundry: z.boolean().optional(),
  public_bathrooms: z.boolean().optional(),
  hot_showers: z.boolean().optional(),
  public_kitchen: z.boolean().optional(),
  has_playground: z.boolean().optional(),
  has_propane_fill_up: z.boolean().optional(),
  has_volleyball_court: z.boolean().optional(),
  has_basketball_court: z.boolean().optional(),
  disc_golf_course: z.boolean().optional(),
  has_golf_course: z.boolean().optional(),
  has_mini_golf_course: z.boolean().optional(),
  has_restaurant: z.boolean().optional(),
  has_community_fire_pit: z.boolean().optional(),
  dog_park: z.boolean().optional(),
  pool: z.boolean().optional(),
  dump_station: z.boolean().optional(),
  dumpster: z.boolean().optional(),
  general_store: z.boolean().optional(),
  picnic_table: z.boolean().optional(),
  
  // Additional numeric fields from SiteProps.ts
  wifi_extra_charge_cents: z.number().optional(),
  laundry_cost: z.number().optional(),
  
  // String fields from SiteProps.ts
  badge_label: z.string().nullable().optional(),
  showers: z.string().nullable().optional(),
  checkin_time: z.string().nullable().optional(),
  checkout_time: z.string().nullable().optional(),
  
  // Additional fields that might be needed
  pad_type: z.string().nullable().optional(),
  is_available: z.boolean().optional(),
  last_meter_reading: z.number().optional(),
  restricted_rig_types: z.array(z.string()).optional(),
  wild_energy_meter_location_id: z.string().nullable().optional(),
  vendor_calendars: z.array(z.object({
    id: z.string(),
    site_id: z.string(),
    kind: z.string(),
    calendar_url: z.string(),
  })).optional(),
}).passthrough(); // Allow additional fields that might be present

export type EditSiteFormType = z.infer<typeof EditSiteFormSchema>;